import { FC, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { useAppSelector } from 'hooks/redux';
import { useTranslations } from 'hooks/useTranslations';
import { Pagination } from 'models/common';
import { localizationSelector } from 'store/localizationState';
import { useGetRegisteredProductListQuery } from 'store/productService';
import * as routes from 'router/routes';
import { disableProductRegistration } from 'config';

import SeoHelmet from 'components/fragments/SeoHelmet';
import Button from 'components/UI/Buttons/Button';
import FloatingActionButton from 'components/UI/Buttons/FloatingActionButton';
import Copyright from 'components/UI/Copyright';
import Heading from 'components/UI/Heading';
import Loader from 'components/UI/Loader';
import { IconType } from 'components/UI/Icon';
import PageState from 'components/UI/Page/PageState';
import PageWrapper from 'components/UI/Page/PageWrapper';
import ProductCard from 'components/UI/Product/ProductCard';
import ProductCardList from 'components/UI/Product/ProductCardList';

import {
  HeadingContainer,
  ActionButtonContainer,
  Description,
  LoadedText,
  LoadMoreContainer
} from './styled';

const ProductsPage: FC = () => {
  const t = useTranslations();
  const navigate = useNavigate();
  const { languageCode } = useAppSelector(localizationSelector);

  // State
  const [pagination, setPagination] = useState<Pagination>({
    limit: 25,
    cursor: null
  });

  // Hooks
  const { data, isLoading, isFetching } = useGetRegisteredProductListQuery({
    languageCode,
    pagination
  });

  // Callbacks
  const onRegisterProductClick = useCallback(
    () => navigate(routes.PRODUCT_REG),
    [navigate]
  );
  const onProductClick = useCallback(
    (iprId: string) => () => navigate(`${routes.PRODUCTS}/${iprId}`),
    [navigate]
  );
  const onClickLoadMore = useCallback(() => {
    setPagination((prev) => {
      return {
        ...prev,
        cursor: data?.nextCursor ?? prev.cursor
      };
    });
  }, [data]);

  // Products
  const productCards = useMemo(() => {
    // Loading
    if (isLoading) {
      return <Loader padding center size="large" />;
    }

    // Empty state when product registration is disabled
    if (!data?.productRegistrations.length && disableProductRegistration) {
      return (
        <PageState
          iconType={IconType.Products}
          title={t('myproducts.no_products_title')}
        />
      );
    }

    // Empty state
    if (!data?.productRegistrations.length) {
      return (
        <PageState
          iconType={IconType.Products}
          title={t('myproducts.no_products_title')}
          description={t('myproducts.no_products_description')}
        >
          <Button size="large" onClick={onRegisterProductClick}>
            {t('myproducts.btn_add_product')}
          </Button>
        </PageState>
      );
    }

    // Data
    return (
      <ProductCardList>
        {data.productRegistrations.map(({ product }) => (
          <ProductCard
            key={product.iprId}
            product={product}
            onClick={onProductClick(product.iprId)}
            showOfferings
          />
        ))}
      </ProductCardList>
    );
  }, [data, isLoading, t, onProductClick, onRegisterProductClick]);

  // Loaded text
  const loadedText = useMemo(() => {
    if (!data?.productRegistrations.length) {
      return null;
    }

    return (
      <LoadedText>
        {t('myproducts.loaded', { total: data.productRegistrations.length })}
      </LoadedText>
    );
  }, [data, t]);

  // Load more
  const loadMore = useMemo(() => {
    if (!data?.nextCursor) {
      return null;
    }

    if (isFetching) {
      return <Loader center padding size="large" />;
    }

    // No more data to load
    if (data.nextCursor === pagination.cursor) {
      return null;
    }

    return (
      <LoadMoreContainer>
        <Button size="large" onClick={onClickLoadMore}>
          {t('myproducts.btn_load_more')}
        </Button>
      </LoadMoreContainer>
    );
  }, [onClickLoadMore, data, pagination, isFetching, t]);

  return (
    <PageWrapper>
      <Copyright>
        <SeoHelmet title="myproducts.meta_title" />
        <HeadingContainer>
          <Heading>{t('myproducts.title')}</Heading>
          {!!data?.productRegistrations.length && (
            <h6>{`(${data.productRegistrations.length})`}</h6>
          )}
        </HeadingContainer>
        <Description>{t('myproducts.description')}</Description>
        {productCards}
        {loadedText}
        {loadMore}
      </Copyright>
      {!!data?.productRegistrations.length && !disableProductRegistration && (
        <ActionButtonContainer>
          <FloatingActionButton
            text={t('myproducts.btn_add_product')}
            onClick={onRegisterProductClick}
          />
        </ActionButtonContainer>
      )}
    </PageWrapper>
  );
};

export default ProductsPage;
